import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers, requestMiddleware } from '@/v2/constants/requests'

export const useMessages = (conversationId, page = 1) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['Messages', conversationId, page],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/conversations/${conversationId}/messages?page=${page}`
      )
      if (!res.ok) {
        throw new Error('We could not find messages')
      }
      const result = await res.json()

      return result
    },
    enabled: !!conversationId,
  })

  return { messageHistory: data, isLoading, isError }
}

// Puts a message at the top of the first page the Conversation endpoint
// returned, once: the reply of the send request and the WebSocket event both
// carry it, and either can arrive first.
export const addMessageToConversation = (
  queryClient,
  conversationId,
  message
) =>
  queryClient.setQueryData(
    ['Conversation', `${conversationId}`],
    conversation => {
      if (!conversation?.messages) return conversation
      const { messages, ...others } = conversation
      const { records, ...pagesInfo } = messages
      if (records.some(({ id }) => id === message.id)) return conversation

      return {
        ...others,
        messages: { ...pagesInfo, records: [message, ...records] },
      }
    }
  )

export const useSendMessage = conversationId => {
  const queryClient = useQueryClient()
  const { mutate: sendMessage, ...others } = requestMiddleware({
    key: ['SendMessage', conversationId],
    // The request answers with the message it created. Showing it from here
    // keeps the sender's own message on screen when the WebSocket is down.
    onSuccess: message => {
      if (message?.id)
        addMessageToConversation(queryClient, conversationId, message)
    },
    request: async message =>
      await fetch(
        `${baseURL}/v2/rest/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ message: { body: message } }),
        }
      ),
  })

  return { sendMessage, ...others }
}

export const useDeleteMessage = (conversationId, messageId) => {
  const { mutate: deleteMessage, ...others } = requestMiddleware({
    key: ['DeleteMessage', conversationId, messageId],
    request: async () =>
      await fetch(`${baseURL}/v2/rest/messages/${messageId}`, {
        method: 'DELETE',
        headers,
      }),
  })

  return { deleteMessage, ...others }
}
